import { useState } from "react";
import { useParams } from "react-router-dom";
import { SeedingConfirmModal } from "../../components/SeedingConfirmModal";
import { useAppState } from "../../context/AppStateContext";
import { triSelect, workmanCardSelect } from "../formUtils";
import { isWorkmanCardPassing } from "../../types";

export function ValidationTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, updateValidations, setHoldReasons, confirmSeeding } = useAppState();
  const app = id ? getById(id) : undefined;
  const [seedingModal, setSeedingModal] = useState(false);

  if (!app) return null;
  const v = app.validations;
  const coreBlocking =
    v.nameMatchAcrossSources !== true ||
    v.aadhaarVerified !== true ||
    v.bankAccountVerified !== true ||
    !isWorkmanCardPassing(v.workmanCardAcceptable);

  const syncHoldFromRules = () => {
    const reasons: string[] = [];
    if (v.nameMatchAcrossSources === false) {
      reasons.push(
        "Beneficiary name does not match exactly across Aadhaar, bank records, and sanction copy — on hold until rectified."
      );
    }
    if (v.workmanCardAcceptable === false) {
      reasons.push(
        "Workman card not acceptable or missing — on hold until a valid card is received."
      );
    }
    void setHoldReasons(app.id, reasons);
  };

  return (
    <>
      <p className="tab-intro">
        <strong>What you do here:</strong> Record each check using the dropdowns. If something is wrong, choose “Issue /
        mismatch” and use the button to put the file on hold with a clear reason. When everything is OK, confirm Aadhaar
        seeding — then you can open the <strong>ICICI &amp; MOU</strong> tab.
      </p>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Checklist (mark each item)</h2>
        <ul className="checklist">
          <li>
            <div className="checklist__body">
              <strong>Name match</strong>
              <div className="checklist__hint">Must match exactly across Aadhaar, bank, and sanction copy.</div>
            </div>
            {triSelect(v.nameMatchAcrossSources, (nv) => void updateValidations(app.id, { nameMatchAcrossSources: nv }), "name")}
          </li>
          <li>
            <div className="checklist__body">
              <strong>Aadhaar number</strong>
              <div className="checklist__hint">Verify and correct through official process if wrong.</div>
            </div>
            {triSelect(v.aadhaarVerified, (nv) => void updateValidations(app.id, { aadhaarVerified: nv }), "aadhaar")}
          </li>
          <li>
            <div className="checklist__body">
              <strong>Bank account number</strong>
              <div className="checklist__hint">Verify against passbook / bank records.</div>
            </div>
            {triSelect(v.bankAccountVerified, (nv) => void updateValidations(app.id, { bankAccountVerified: nv }), "bank")}
          </li>
          <li>
            <div className="checklist__body">
              <strong>Workman card</strong>
              <div className="checklist__hint">If not available, place file on hold until received.</div>
            </div>
            {workmanCardSelect(v.workmanCardAcceptable, (nv) => void updateValidations(app.id, { workmanCardAcceptable: nv }), "wm")}
          </li>
        </ul>
        <div className="toolbar-row">
          <button type="button" className="btn btn--primary btn--tg" onClick={syncHoldFromRules}>
            Set “on hold” from checklist (if any item is “Issue”)
          </button>
          <button
            type="button"
            className="btn btn--tg-secondary"
            onClick={() => void setHoldReasons(app.id, [])}
            disabled={app.holdReasons.length === 0}
          >
            Remove all hold reasons
          </button>
        </div>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Aadhaar seeding — final OK before banking</h2>
        <p className="section-lead">
          Only tick this after the checklist above is complete. A short confirmation window will open; after you confirm,
          the ICICI tab unlocks.
        </p>
        <p className="page-meta">
          Status:{" "}
          {v.aadhaarSeedingConfirmed ? (
            <span className="badge badge--ok">Confirmed</span>
          ) : (
            <span className="badge badge--pending">Not confirmed</span>
          )}
          {v.seedingConfirmedAt ? (
            <span className="mono text-muted" style={{ marginLeft: "0.5rem", fontSize: "0.8125rem" }}>
              {v.seedingConfirmedAt}
            </span>
          ) : null}
        </p>
        <button
          type="button"
          className="btn btn--primary btn--tg"
          onClick={() => setSeedingModal(true)}
          disabled={v.aadhaarSeedingConfirmed || coreBlocking}
        >
          I confirm Aadhaar seeding (opens confirmation)
        </button>
        {coreBlocking ? (
          <p className="text-warning" style={{ fontSize: "0.8125rem", marginTop: "0.75rem" }}>
            Complete all checkpoints positively before seeding confirmation.
          </p>
        ) : null}
      </div>

      <SeedingConfirmModal
        open={seedingModal}
        title="Confirm Aadhaar seeding"
        body="I confirm that Aadhaar seeding has been verified for this beneficiary as per programme rules. ICICI banking details shall not be entered until this confirmation is recorded."
        confirmLabel="Record confirmation"
        onCancel={() => setSeedingModal(false)}
        onConfirm={() => {
          void (async () => {
            try {
              await confirmSeeding(app.id);
            } catch {
              /* confirmSeeding shows error toast */
            } finally {
              setSeedingModal(false);
            }
          })();
        }}
      />
    </>
  );
}
